/**
 * The scene tools every editing host's assistant calls (see `types.ts`).
 *
 * Import from `trace-viewer/editor`.
 */

import { ARRANGE_TOOLS } from './arrangeTools';
import { ASSET_TOOLS } from './assetTools';
import { BEHAVIOR_TOOLS } from './behaviorTools';
import { EDIT_TOOLS } from './editTools';
import { OBJECT_TOOLS } from './objectTools';
import { READ_TOOLS } from './readTools';
import type { SceneTool } from './types';
import { UNDO_TOOLS } from './undoTools';
import { VIEW_TOOLS } from './viewTools';
import { WIRING_TOOLS } from './wiringTools';

export const SCENE_TOOLS: readonly SceneTool[] = [...OBJECT_TOOLS, ...EDIT_TOOLS, ...ARRANGE_TOOLS, ...READ_TOOLS, ...BEHAVIOR_TOOLS, ...WIRING_TOOLS, ...UNDO_TOOLS, ...VIEW_TOOLS, ...ASSET_TOOLS];

/** The scene tool named `name`, or undefined. */
export function sceneTool(name: string): SceneTool | undefined {
    return SCENE_TOOLS.find(tool => tool.name === name);
}

/** Reading `[x, y, z]` input, for a host's own tools that take a pose. */
export { readTransformArgs, readVec3 } from './input';
/** What draws under an object, boxed in its parent's frame: build_spatial's snapshot `bounds`. */
export { boundsInParent } from './input';
/** An object's connector anchors as the tools show them, for a host's own readouts. */
export { describeAnchors } from './readTools';
/** What the user has open, for a host's per-turn context. */
export { describeEditorFocus } from './focus';
/** The scene as get_scene_tree lists it, for a host's per-turn outline. */
export { describeSceneTree, type SceneTreeOptions } from './readTools';
/** A behavior's data as the tools show it, for a host that shows a model one (a specialist's context). */
export { behaviorDataText } from './behaviorTools';
/** A tool's interaction as `wireInteractions` takes it, for a host that compiles one itself. */
export { pickabilityWarning, readInteraction } from './wiringTools';
export type {
    AssetImport, CaptureRequest, JsonSchema, SceneTool, SceneToolHost, ToolAnnotations, ToolDef, ToolImage, ToolInputSchema, ToolMeta, ToolOutcome,
} from './types';
/** The named camera angles focus_camera and screenshot take, for a host that renders from them. */
export { CAMERA_ANGLE_PRESETS } from './viewTools';
/** Where a capture is seen from, as arithmetic on a bounding box, for a host that renders headless. */
export {
    DEFAULT_PITCH, DEFAULT_RADIUS_SCALE, DEFAULT_YAW, alphaBetaToYawPitch, boundsAreFinite, boundsCenter, boundsDiagonal, clampPitch,
    describePose, fitOrbitRadius, normalizeYaw, orbitCameraPosition, orbitPoseFromBounds, poseFromLookAt, poseToAlphaBeta,
    type CapturePose, type OrbitPoseOptions, type WorldBounds,
} from '../camera/capturePose';
